import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Star, LogOut, Loader2, TrendingUp, Plus, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AdminMetaScores() {
  const { user, loading: authLoading, logout } = useAuth('admin_user');
  const [scores, setScores] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [newScore, setNewScore] = useState({ tenant_id: '', source: 'google', value: '', count: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      loadTenants();
      loadScores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) loadScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  const loadTenants = async () => {
    try {
      const res = await adminApi.getTenants();
      setTenants(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadScores = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedTenant && selectedTenant !== 'all') params.tenant_id = selectedTenant;
      const res = await adminApi.getMetaScores(params);
      setScores(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScore = async () => {
    if (!newScore.tenant_id || !newScore.value) return;
    setSubmitting(true);
    try {
      await adminApi.createMetaScore(newScore.tenant_id, {
        source: newScore.source,
        value: parseFloat(newScore.value),
        count: newScore.count ? parseInt(newScore.count) : null
      });
      toast.success('MetaScore erstellt!');
      setCreateOpen(false);
      setNewScore({ tenant_id: '', source: 'google', value: '', count: '' });
      loadScores();
    } catch (err) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Datum', 'Tenant', 'Quelle', 'Score', 'Anzahl'];
    const rows = scores.map(s => {
      const tenant = tenants.find(t => t.id === s.tenant_id);
      return [
        s.captured_at ? format(new Date(s.captured_at), 'dd.MM.yyyy') : '',
        tenant?.name || s.tenant_id,
        s.source,
        s.value,
        s.count || ''
      ].join(';');
    });
    
    const csv = [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `metascores_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('CSV exportiert');
  };

  // Prepare chart data - group by tenant
  const chartData = React.useMemo(() => {
    const grouped = {};
    scores.forEach(s => {
      const dateKey = s.captured_at ? format(new Date(s.captured_at), 'yyyy-MM') : 'unknown';
      if (!grouped[dateKey]) {
        grouped[dateKey] = { date: dateKey };
      }
      const tenant = tenants.find(t => t.id === s.tenant_id);
      const tenantName = tenant?.name?.substring(0, 15) || 'Unknown';
      grouped[dateKey][tenantName] = s.value;
    });
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [scores, tenants]);

  // Get unique tenant names for chart legend
  const tenantNames = React.useMemo(() => {
    const names = new Set();
    scores.forEach(s => {
      const tenant = tenants.find(t => t.id === s.tenant_id);
      if (tenant) names.add(tenant.name?.substring(0, 15));
    });
    return Array.from(names);
  }, [scores, tenants]);

  const colors = ['#2155a6', '#00bcd4', '#fca311', '#34a853', '#ea4335', '#9c27b0'];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-primary text-primary-foreground border-b">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5" />
              </div>
              <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita Admin</span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-sm opacity-80 hover:opacity-100">Übersicht</Link>
            <Link to="/admin/tenants" className="text-sm opacity-80 hover:opacity-100">Tenants</Link>
            <Link to="/admin/reviews" className="text-sm opacity-80 hover:opacity-100">Bewertungen</Link>
            <Link to="/admin/prompts" className="text-sm opacity-80 hover:opacity-100">Prompts</Link>
            <Link to="/admin/coupons" className="text-sm opacity-80 hover:opacity-100">Gutscheine</Link>
            <Link to="/admin/meta-scores" className="text-sm font-medium">MetaScores</Link>
          </nav>
          <Button variant="ghost" size="icon" onClick={logout} className="text-primary-foreground hover:bg-white/20">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold">MetaScore Tracking</h1>
            <p className="text-muted-foreground">Externe Bewertungen von Google, Trustpilot etc.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={exportCSV} data-testid="metascores-export-button">
              <Download className="w-4 h-4 mr-2" />
              CSV Export
            </Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="metascores-add-button">
              <Plus className="w-4 h-4 mr-2" />
              Score erfassen
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <div className="w-64">
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger>
                <SelectValue placeholder="Alle Tenants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Tenants</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <Card className="card-shadow mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Score-Entwicklung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(val) => {
                        const [year, month] = val.split('-');
                        return `${month}/${year.slice(2)}`;
                      }}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(val) => [`${val.toFixed(1)}/5`, '']}
                    />
                    <Legend />
                    {tenantNames.map((name, i) => (
                      <Line 
                        key={name}
                        type="monotone" 
                        dataKey={name} 
                        stroke={colors[i % colors.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Alle Einträge ({scores.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : scores.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">Noch keine MetaScores erfasst</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Quelle</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Anzahl Bewertungen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scores.map((score) => {
                    const tenant = tenants.find(t => t.id === score.tenant_id);
                    return (
                      <TableRow key={score.id}>
                        <TableCell>
                          {score.captured_at 
                            ? format(new Date(score.captured_at), 'dd.MM.yyyy', { locale: de })
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Link to={`/admin/tenants/${score.tenant_id}`} className="hover:underline">
                            {tenant?.name || score.tenant_id}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{score.source}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]" />
                            <span className="font-medium">{score.value?.toFixed(1)}</span>
                            <span className="text-muted-foreground">/5</span>
                          </div>
                        </TableCell>
                        <TableCell>{score.count || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>MetaScore erfassen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Tenant</label>
                <Select 
                  value={newScore.tenant_id} 
                  onValueChange={(v) => setNewScore(prev => ({ ...prev, tenant_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tenant wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Quelle</label>
                <Select 
                  value={newScore.source} 
                  onValueChange={(v) => setNewScore(prev => ({ ...prev, source: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google</SelectItem>
                    <SelectItem value="trustpilot">Trustpilot</SelectItem>
                    <SelectItem value="other">Andere</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Score (0-5)</label>
                <Input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={newScore.value}
                  onChange={(e) => setNewScore(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="z.B. 4.2"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Anzahl Bewertungen (optional)</label>
                <Input
                  type="number"
                  value={newScore.count}
                  onChange={(e) => setNewScore(prev => ({ ...prev, count: e.target.value }))}
                  placeholder="z.B. 45"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
              <Button
                onClick={handleCreateScore}
                disabled={submitting || !newScore.tenant_id || !newScore.value}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
